import i18n from "../../../i18n";
import React, { useState, useEffect } from "react";
import { useFormatter } from "../../../hooks/useFomatter";
import { useServerApi } from "../../../hooks/userServerApi";
import { IconSearch } from "@tabler/icons";
import {
  Button,
  Text,
  Title,
  Badge,
  Accordion,
  LoadingOverlay,
  Box,
  Paper,
  Table,
  Group,
  Stack,
} from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import moment from "moment";
import EntityTabAnchor from "./EntityTabAnchor";
import DuplicateBidConflictsAlert from "./DuplicateBidConflictsAlert";
import {
  downloadExcel,
  matchesQuery,
  payrollFileName,
  ResultTools,
} from "./listExport";

const CommissionStatementsTab = ({ form }) => {
  const [api] = useServerApi();
  const [formatter] = useFormatter();

  const [loading, setLoading] = useState(false);
  const [fromPeriod, setFromPeriod] = useState(null);
  const [toPeriod, setToPeriod] = useState(null);
  const [statements, setStatements] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [openStatementId, setOpenStatementId] = useState(null);
  const [query, setQuery] = useState("");

  const payrollStatus = form?.values?.status;
  const isConfirmed =
    payrollStatus === "CONFIRMED" || payrollStatus === "COMPLETED";

  const loadSkippedDuplicateConflicts = async () => {
    if (!form?.values?._id) {
      setConflicts([]);
      return;
    }
    try {
      const result = await api.Payroll2.getDuplicateBidConflicts(
        form.values._id,
      );
      if (result?.success) {
        setConflicts(
          result.data?.excludedConflicts ||
            result.data?.skippedDuplicateSources ||
            result.data?.conflicts ||
            [],
        );
      }
    } catch (error) {
      console.log("loadSkippedDuplicateConflicts error:", error);
    }
  };

  useEffect(() => {
    loadSkippedDuplicateConflicts();
  }, [form?.values?._id]);

  useEffect(() => {
    const savedSearch = form?.values?.commissionStatementSearch;
    if (savedSearch?.fromPeriod) setFromPeriod(new Date(savedSearch.fromPeriod));
    if (savedSearch?.toPeriod) setToPeriod(new Date(savedSearch.toPeriod));

    const payrollId = form?.values?._id;
    const savedStatementIds = form?.values?.commissionStatements;
    if (!payrollId || !savedStatementIds?.length) return;

    let cancelled = false;
    const loadSavedStatements = async () => {
      try {
        setLoading(true);
        const result =
          await api.Payroll2.getSavedCommissionStatementsForPayroll(payrollId);
        if (cancelled) return;
        if (result?.success && result.data?.statements) {
          setStatements(result.data.statements);
        }
      } catch (error) {
        console.log("Error loading saved statements:", error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadSavedStatements();
    return () => {
      cancelled = true;
    };
  }, [form?.values?._id]);

  const saveStatementsToPayroll = async (statementIds, rowIds) => {
    if (!form?.values?._id || !form?.updateAndSave) return;
    try {
      await form.updateAndSave({
        commissionStatements: statementIds,
        commissionStatementRows: rowIds,
        commissionStatementSearch: {
          fromPeriod,
          toPeriod,
        },
      });
    } catch (error) {
      console.log("Error saving commission statements to payroll:", error);
    }
  };

  const handleSearch = async () => {
    if (!fromPeriod || !toPeriod) {
      return;
    }

    try {
      setLoading(true);
      const result = await api.Payroll2.getCommissionStatementsForPayroll(
        moment(fromPeriod).format("YYYY-MM-DD"),
        moment(toPeriod).format("YYYY-MM-DD"),
        form?.values?._id,
      );

      if (result && result.success && result.data && result.data.statements) {
        setStatements(result.data.statements);
        setConflicts(result.data.excludedConflicts || result.data.conflicts || []);
        const statementIds = result.data.statements.map((s) => s._id);
        const rowIds = result.data.statements.flatMap((statement) =>
          (statement.rows || []).map((row) => row._id).filter(Boolean),
        );
        await saveStatementsToPayroll(statementIds, rowIds);
        console.log(
          `Loaded ${result.data.count} commission statements (no pagination limit)`,
        );
      } else {
        setStatements([]);
        setConflicts([]);
      }
    } catch (error) {
      console.log("Error fetching commission statements:", error);
      setStatements([]);
    } finally {
      setLoading(false);
    }
  };

  const getConfirmedRowCount = (rows) => {
    if (!rows) return 0;
    return rows.filter((r) => r.status === "CONFIRMED").length;
  };

  const getConfirmedCommission = (rows) => {
    if (!rows) return 0;
    return rows
      .filter((r) => r.status === "CONFIRMED")
      .reduce((sum, r) => sum + (r.totalRcv || 0), 0);
  };

  const filteredStatements = statements.filter((statement) =>
    matchesQuery(query, [
      statement.code,
      statement.period,
      statement.status,
      statement.provider?.name,
      statement.provider?.code,
      ...(statement.rows || []).flatMap((row) => [
        row.order?.code,
        row.order?.debitNote?.code,
        row.policy,
        row.insuredName,
        row.order?.client?.name,
        row.order?.client?.code,
        row.order?.debitNote?.productType?.name,
        row.planCode,
        row.status,
        row.rowNumber,
        row.internalRemark,
      ]),
    ]),
  );

  const downloadStatements = () => {
    const rows = [];
    statements.forEach((statement) => {
      const statementRows = statement.rows || [];
      if (!statementRows.length) {
        rows.push([
          statement.code || "",
          statement.period || "",
          statement.provider?.name || statement.provider?.code || "",
          statement.status || "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
        ]);
        return;
      }
      statementRows.forEach((row, index) => {
        rows.push([
          statement.code || "",
          statement.period || "",
          statement.provider?.name || statement.provider?.code || "",
          statement.status || "",
          row.rowNumber || index + 1,
          row.order?.code || "",
          row.order?.debitNote?.code || "",
          row.policy || "",
          row.insuredName ||
            row.order?.client?.name ||
            row.order?.client?.code ||
            "",
          row.order?.debitNote?.productType?.name ||
            row.order?.product?.type?.name ||
            row.planCode ||
            row.order?.product?.name ||
            "",
          Number(row.premiumReceived) || 0,
          Number(row.totalRcv) || 0,
          row.status || "PENDING",
          row.internalRemark || "",
        ]);
      });
    });
    downloadExcel(
      payrollFileName("commission-statements", form?.values?.code),
      [
        "Statement",
        "Period",
        "Provider",
        "Statement Status",
        "Row",
        "Order",
        "Debit Note",
        "Policy",
        "Client / Insured",
        "Category",
        "Premium",
        "Total Rcv",
        "Row Status",
        "Remark",
      ],
      rows,
    );
  };

  return (
    <Box pos="relative">
      <LoadingOverlay visible={loading} overlayBlur={2} />

      <Paper p="md" withBorder mb="lg">
        <Title order={5} mb="md">
          Search Confirmed Commission Statements
        </Title>

        <Group align="flex-end" spacing="md">
          <DatePicker
            label="Period From"
            size="xs"
            placeholder="Select start month"
            value={fromPeriod}
            onChange={setFromPeriod}
            allowFreeInput
            clearable
            disabled={isConfirmed}
          />

          <DatePicker
            label="Period To"
            size="xs"
            placeholder="Select end month"
            value={toPeriod}
            onChange={setToPeriod}
            allowFreeInput
            clearable
            disabled={isConfirmed}
          />

          <Button
            size="xs"
            leftIcon={<IconSearch size={14} />}
            onClick={handleSearch}
            disabled={!fromPeriod || !toPeriod || isConfirmed}
          >{i18n.t("Search")}</Button>
        </Group>

        <Text size="xs" color="dimmed" mt="xs">
          Note: The range is each row's confirmed date. Rows confirmed before
          that date was stored still use the statement confirmed date.
        </Text>
        {isConfirmed && (
          <Text size="xs" color="orange" mt="xs">
            Payroll is completed. No more edits allowed.
          </Text>
        )}
      </Paper>

      <Box mb="md">
        <DuplicateBidConflictsAlert conflicts={conflicts} />
      </Box>

      {statements.length > 0 && (
        <Paper p="md" withBorder>
          <Group position="apart" mb="md">
            <Title order={5}>{i18n.t("Results")}</Title>
            <Group spacing="sm">
              <ResultTools
                query={query}
                onQueryChange={(value) => {
                  setQuery(value);
                  setOpenStatementId(null);
                }}
                onDownload={downloadStatements}
                placeholder={i18n.t("Code, client, order")}
              />
              <Badge size="lg">
                {query.trim()
                  ? `${filteredStatements.length} / ${statements.length}`
                  : `${statements.length} statements found`}
              </Badge>
            </Group>
          </Group>

          {filteredStatements.length === 0 ? (
            <Text color="dimmed" align="center">
              No statements match this search.
            </Text>
          ) : (
          <Accordion
            variant="separated"
            value={openStatementId}
            onChange={setOpenStatementId}
          >
            {filteredStatements.map((statement) => (
              <Accordion.Item key={statement._id} value={statement._id}>
                <Accordion.Control>
                  <Group position="apart" noWrap>
                    <Group spacing="xl" noWrap>
                      <Box w={120}>
                        <EntityTabAnchor
                          entity="commissionStatement"
                          id={statement._id}
                          label={statement.code}
                          size="sm"
                          weight={600}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <Text size="xs" color="dimmed">
                          {statement.period}
                        </Text>
                      </Box>

                      <Box w={200}>
                        <Text size="sm" weight={500} lineClamp={1}>
                          {statement.provider?.name ||
                            statement.provider?.code ||
                            "-"}
                        </Text>
                        <Text size="xs" color="dimmed">{i18n.t("Provider")}</Text>
                      </Box>

                      <Box w={80}>
                        <Group spacing={4}>
                          <Badge color="green" size="sm">
                            {getConfirmedRowCount(statement.rows)}
                          </Badge>
                          <Text size="xs" color="dimmed">
                            / {statement.rows?.length || 0}
                          </Text>
                        </Group>
                        <Text size="xs" color="dimmed">
                          Rows
                        </Text>
                      </Box>

                      <Box w={120}>
                        <Text size="sm" weight={500} color="green">
                          {formatter.currency(
                            getConfirmedCommission(statement.rows),
                          )}
                        </Text>
                        <Text size="xs" color="dimmed">{i18n.t("Confirmed")}</Text>
                      </Box>
                    </Group>

                    <Badge color="green" size="sm">
                      {statement.status}
                    </Badge>
                  </Group>
                </Accordion.Control>

                <Accordion.Panel>
                  {openStatementId === statement._id && (
                  <Stack spacing="md" mt="md">
                    <Group spacing="xl">
                      <Box>
                        <Text size="xs" color="dimmed">{i18n.t("Total Commission")}</Text>
                        <Text size="sm" weight={500}>
                          {formatter.currency(statement.totalCommission || 0)}
                        </Text>
                      </Box>
                      <Box>
                        <Text size="xs" color="dimmed">{i18n.t("Confirmed Commission")}</Text>
                        <Text size="sm" weight={500} color="green">
                          {formatter.currency(
                            getConfirmedCommission(statement.rows),
                          )}
                        </Text>
                      </Box>
                      <Box>
                        <Text size="xs" color="dimmed">{i18n.t("Total Rows")}</Text>
                        <Text size="sm">{statement.rows?.length || 0}</Text>
                      </Box>
                      <Box>
                        <Text size="xs" color="dimmed">
                          Confirmed Rows
                        </Text>
                        <Badge color="green" size="sm">
                          {getConfirmedRowCount(statement.rows)}
                        </Badge>
                      </Box>
                    </Group>

                    {statement.rows && statement.rows.length > 0 && (
                      <>
                        <Text size="sm" weight={500}>
                          Statement Rows:
                        </Text>
                        <Table fontSize="xs" striped highlightOnHover>
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>{i18n.t("Order")}</th>
                              <th>DN</th>
                              <th>{i18n.t("Policy")}</th>
                              <th>{i18n.t("Client / Insured")}</th>
                              <th>{i18n.t("Category")}</th>
                              <th>{i18n.t("Premium")}</th>
                              <th>{i18n.t("Total Rcv")}</th>
                              <th>{i18n.t("Status")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {statement.rows.map((row, idx) => (
                              <React.Fragment key={idx}>
                                <tr>
                                  <td>{row.rowNumber || idx + 1}</td>
                                  <td>
                                    <EntityTabAnchor
                                      entity="order"
                                      id={row.order?._id}
                                      label={row.order?.code}
                                    />
                                  </td>
                                  <td>
                                    <EntityTabAnchor
                                      entity="debitNote"
                                      id={row.order?.debitNote?._id}
                                      label={row.order?.debitNote?.code}
                                    />
                                  </td>
                                  <td>
                                    <Text size="xs">{row.policy || "-"}</Text>
                                  </td>
                                  <td>
                                    <Text size="xs" lineClamp={1}>
                                      {row.insuredName ||
                                        row.order?.client?.name ||
                                        row.order?.client?.code ||
                                        "-"}
                                    </Text>
                                  </td>
                                  <td>
                                    <Text size="xs" lineClamp={1}>
                                      {row.order?.debitNote?.productType
                                        ?.name ||
                                        row.order?.product?.type?.name ||
                                        row.planCode ||
                                        row.order?.product?.name ||
                                        "-"}
                                    </Text>
                                  </td>
                                  <td>
                                    <Text size="xs">
                                      {formatter.currency(
                                        row.premiumReceived || 0,
                                      )}
                                    </Text>
                                  </td>
                                  <td>
                                    <Text size="xs" weight={500}>
                                      {formatter.currency(row.totalRcv || 0)}
                                    </Text>
                                  </td>
                                  <td>
                                    <Badge
                                      color={
                                        row.status === "CONFIRMED"
                                          ? "green"
                                          : row.status === "VERIFIED"
                                            ? "blue"
                                            : "gray"
                                      }
                                      size="xs"
                                    >
                                      {row.status || "PENDING"}
                                    </Badge>
                                  </td>
                                </tr>
                                {row.internalRemark?.trim() && (
                                  <tr>
                                    <td colSpan={9} style={{ paddingTop: 0 }}>
                                      <Text
                                        size="xs"
                                        color="dimmed"
                                        italic
                                        lineClamp={2}
                                        title={row.internalRemark.trim()}
                                      >
                                        {row.internalRemark.trim()}
                                      </Text>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            ))}
                          </tbody>
                        </Table>
                      </>
                    )}
                  </Stack>
                  )}
                </Accordion.Panel>
              </Accordion.Item>
            ))}
          </Accordion>
          )}
        </Paper>
      )}

      {statements.length === 0 && !loading && (
        <Paper p="xl" withBorder>
          <Text color="dimmed" align="center">
            No confirmed statements found. Please enter period range and click
            Search.
          </Text>
        </Paper>
      )}
    </Box>
  );
};

export default CommissionStatementsTab;
